import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Package,
  Plus,
  Trash2,
  Wrench,
  Boxes,
  RefreshCw,
  MessageSquare,
  ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import {
  addInventoryItem,
  inventoryCounts,
  listInventory,
  removeInventoryItem,
  updateInventoryItem,
  type InventoryItem,
  type InventoryKind,
} from "@/lib/prometheusInventoryStore";
import { OsSyncPanel } from "@/components/prometheus/os/OsSyncPanel";

const KIND_LABEL: Record<InventoryKind, string> = {
  tool: "Herramienta",
  material: "Material",
  consumable: "Consumible",
  other: "Otro",
};

function askAgentAboutInventory() {
  window.dispatchEvent(
    new CustomEvent("prometheus-os-section", { detail: { section: "chats" } })
  );
  window.dispatchEvent(
    new CustomEvent("open-prometheus-chat", {
      detail: {
        characterId: "prometheus",
        initialPrompt:
          "@skill:bom Usa mi inventario real del taller. Dime qué puedo construir hoy con lo que ya tengo y qué me falta comprar en PR (Home Depot / National Lumber / Ace / Mouser) con precios USD.",
        autoSend: true,
      },
    })
  );
}

export function InventarioPanel() {
  const [items, setItems] = useState<InventoryItem[]>([]);
  const [name, setName] = useState("");
  const [kind, setKind] = useState<InventoryKind>("tool");
  const [qty, setQty] = useState(1);
  const [filter, setFilter] = useState<"all" | InventoryKind>("all");

  const reload = useCallback(() => {
    setItems(listInventory());
  }, []);

  useEffect(() => {
    reload();
    const sync = () => reload();
    window.addEventListener("prometheus-inventory-changed", sync);
    return () => window.removeEventListener("prometheus-inventory-changed", sync);
  }, [reload]);

  const counts = inventoryCounts();
  const visible =
    filter === "all" ? items : items.filter((i) => i.kind === filter);

  const onAdd = (e: React.FormEvent) => {
    e.preventDefault();
    const item = addInventoryItem({ name, kind, qty, source: "manual" });
    if (!item) {
      toast.message("Nombre demasiado corto");
      return;
    }
    setName("");
    setQty(1);
    reload();
    toast.success(`Añadido: ${item.name}`);
  };

  return (
    <div className="h-full overflow-y-auto bg-void p-4 pt-12 sm:p-6 md:pt-6 font-mono-tech">
      <div className="max-w-3xl mx-auto space-y-5">
        <header className="space-y-2">
          <p className="text-[10px] uppercase tracking-[0.2em] text-primary font-bold">
            Inventario del taller
          </p>
          <h1 className="font-serif-display text-2xl text-foreground">
            Lo que ya tienes · memoria del agente
          </h1>
          <p className="text-sm text-muted-foreground leading-relaxed max-w-2xl">
            Herramientas y materiales en stock (local). El chat prioriza esto antes de
            sugerir compras en PR. BOM marcados inStock se sincronizan solos.
          </p>
        </header>

        <div className="flex flex-wrap gap-2 text-[11px]">
          <span className="rounded-lg border border-border px-2.5 py-1 text-muted-foreground">
            Total {counts.total}
          </span>
          <span className="rounded-lg border border-border px-2.5 py-1 inline-flex items-center gap-1">
            <Wrench className="w-3 h-3" /> {counts.tools} tools
          </span>
          <span className="rounded-lg border border-border px-2.5 py-1 inline-flex items-center gap-1">
            <Boxes className="w-3 h-3" /> {counts.materials} materiales
          </span>
          <button
            type="button"
            onClick={() => {
              reload();
              toast.message("Inventario sincronizado (BOM + local)");
            }}
            className="inline-flex items-center gap-1 rounded-lg border border-border px-2.5 py-1 text-muted-foreground hover:text-foreground cursor-pointer"
          >
            <RefreshCw className="w-3 h-3" /> Sync BOM
          </button>
          <button
            type="button"
            onClick={askAgentAboutInventory}
            className="inline-flex items-center gap-1.5 rounded-lg bg-primary text-primary-foreground px-2.5 py-1 font-bold cursor-pointer hover:opacity-90"
          >
            <MessageSquare className="w-3 h-3" /> Preguntar al chat
          </button>
          <Link
            to="/herramientas?tab=inventory"
            className="inline-flex items-center gap-1.5 rounded-lg border border-border px-2.5 py-1 text-muted-foreground hover:text-foreground"
          >
            <ExternalLink className="w-3 h-3" /> Inventario completo · Abasto PR
          </Link>
        </div>

        <OsSyncPanel compact />

        <form
          onSubmit={onAdd}
          className="rounded-xl border border-border bg-card/50 p-3 flex flex-col sm:flex-row gap-2"
        >
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Ej. Multímetro, PETG-CF 1kg, ESP32-S3…"
            className="flex-1 min-w-0 rounded-lg border border-border bg-background px-3 py-2 text-[13px] text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/50"
          />
          <select
            value={kind}
            onChange={(e) => setKind(e.target.value as InventoryKind)}
            className="rounded-lg border border-border bg-background px-2 py-2 text-[12px] text-foreground cursor-pointer"
          >
            {(Object.keys(KIND_LABEL) as InventoryKind[]).map((k) => (
              <option key={k} value={k}>
                {KIND_LABEL[k]}
              </option>
            ))}
          </select>
          <input
            type="number"
            min={1}
            value={qty}
            onChange={(e) => setQty(Math.max(1, Number(e.target.value) || 1))}
            className="w-16 rounded-lg border border-border bg-background px-2 py-2 text-[12px] text-foreground"
            title="Cantidad"
          />
          <button
            type="submit"
            className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-primary text-primary-foreground px-3 py-2 text-[12px] font-bold cursor-pointer hover:opacity-90"
          >
            <Plus className="w-3.5 h-3.5" />
            Añadir
          </button>
        </form>

        <div className="flex flex-wrap gap-1">
          {(["all", "tool", "material", "consumable", "other"] as const).map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setFilter(f)}
              className={cn(
                "rounded-md border px-2 py-1 text-[10px] cursor-pointer",
                filter === f
                  ? "border-primary/50 bg-primary/15 text-primary font-bold"
                  : "border-border/60 text-muted-foreground hover:text-foreground"
              )}
            >
              {f === "all" ? "Todos" : KIND_LABEL[f]}
            </button>
          ))}
        </div>

        {visible.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border bg-muted/10 px-4 py-10 text-center">
            <Package className="w-7 h-7 text-muted-foreground mx-auto mb-2" />
            <p className="text-[13px] text-muted-foreground">
              Inventario vacío. Añade lo que ya tienes en el taller, o marca BOM inStock en un
              proyecto.
            </p>
          </div>
        ) : (
          <ul className="space-y-1.5">
            {visible.map((item) => (
              <li
                key={item.id}
                className="flex items-center gap-2 rounded-lg border border-border/60 bg-card/40 px-3 py-2"
              >
                <div className="min-w-0 flex-1">
                  <p className="text-[13px] font-medium text-foreground truncate">{item.name}</p>
                  <p className="text-[10px] text-muted-foreground">
                    {KIND_LABEL[item.kind]} · ×{item.qty}
                    {item.unit ? ` ${item.unit}` : ""} · {item.source}
                    {item.projectId ? " · desde BOM" : ""}
                  </p>
                </div>
                <input
                  type="number"
                  min={1}
                  value={item.qty}
                  onChange={(e) => {
                    updateInventoryItem(item.id, {
                      qty: Math.max(1, Number(e.target.value) || 1),
                    });
                    reload();
                  }}
                  className="w-14 rounded-md border border-border bg-background px-1.5 py-1 text-[11px] text-foreground"
                  title="Cantidad"
                />
                <button
                  type="button"
                  onClick={() => {
                    removeInventoryItem(item.id);
                    reload();
                    toast.message(`Quitado: ${item.name}`);
                  }}
                  className="p-1.5 rounded-md text-muted-foreground hover:text-red-400 hover:bg-red-500/10 cursor-pointer"
                  title="Eliminar"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
